import {
  Box,
  Divider,
  Link,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import React from "react";
import {
  ActionTypeCode,
  InformationRequestAction,
} from "../../../domain/InformationRequestAction";
import {
  CheckCircle,
  DescriptionRounded,
  PublishRounded,
  RadioButtonUnchecked,
  SmsRounded,
} from "@material-ui/icons";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { DownloadLink } from "@ofqual/portal-components/components/DownloadLink";
import {
  cachedActionTypesService,
  informationRequestService,
} from "../../../services";
import { useSelector } from "react-redux";
import { authSelector } from "../../../redux/selectors";
import ReactMarkdown from "react-markdown";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { DialogActionArgument, DialogActionType } from "./types";
import { UserType } from "../../../redux/types";
import { DownloadAllLink } from "./DownloadAllLink";
import { MarkDocumentsAsReadLink } from "./MarkDocumentsAsReadLink";

const PADDING = 15;
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 20,
    marginTop: 10,
  },
  activity: {
    flexGrow: 1,
    padding: PADDING,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  avatar: {
    marginLeft: "auto",
    paddingLeft: 15,
    paddingRight: 5,
    paddingTop: 0,
    paddingBottom: 0,
    textAlign: "center",
    lineHeight: "2.5em",
  },
  mainDetails: {
    padding: PADDING,
    flexGrow: 1,
  },
  options: {
    textAlign: "right",
    paddingTop: PADDING,
  },
  option: {
    fontWeight: "bold",
    marginRight: 10,
  },
  iconSection: {
    backgroundColor: "#aaa",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    paddingTop: "12px",
    paddingLeft: "13px",
  },
  docToRead: {
    listStyleType: "none",
    marginBottom: 10,
  },
}));

export type ActionsItemProps = {
  action: InformationRequestAction;
  setFocussedDialog: (dialogActionType: DialogActionArgument) => void;
  isAOView: boolean;
  organisationRecipientId: string;
  disabled?: boolean;
  isSent?: boolean;
};

export const ActionsItem: React.FC<ActionsItemProps> = ({
  action,
  setFocussedDialog,
  isAOView,
  organisationRecipientId,
  disabled,
  isSent,
}) => {
  const classes = useStyles();
  const { userType, token } = useAuth(authSelector);

  const [actionTypesLookups] = useLookup(cachedActionTypesService);
  const actionType = actionTypesLookups.find((x) => x.id === action.actionType);

  const actionTypeName = actionType?.label;
  const actionTypeCode = actionType?.code;

  const buildGetBlob = (blobId: string) => () =>
    informationRequestService.downloadAttachment(
      action.informationRequestId,
      action.id,
      blobId,
      {
        token,
      }
    );

  return actionTypesLookups && actionTypesLookups.length ? (
    <div className={classes.root}>
      <Paper square className={classes.activity} data-test="action">
        <Box display="flex">
          <div className={classes.iconSection}>
            {renderIcon(actionTypeCode)}
          </div>
          <div className={classes.mainDetails}>
            <Typography variant="body1">
              <strong>{actionTypeName}</strong>
            </Typography>
            <div>
              <strong>Due date: </strong>
              {formatUtcDate(action.dueDate, standardDateFormats.DATE)}
            </div>
          </div>
          <div>
            {userType === UserType.AO && (
              <>{action.status ? <CheckCircle /> : <RadioButtonUnchecked />}</>
            )}
          </div>
        </Box>

        {actionTypeCode === ActionTypeCode.ReadDocument ? (
          <ul style={{ padding: 0, marginLeft: 0, marginTop: 0 }}>
            {action.actionAttachments &&
              action.actionAttachments.map((document) => (
                <li key={document.blobId} className={classes.docToRead}>
                  <DownloadLink
                    fileName={document.fileName}
                    getBlob={buildGetBlob(document.blobId)}
                  />
                </li>
              ))}
          </ul>
        ) : (
          <ReactMarkdown>{action.message}</ReactMarkdown>
        )}

        <Divider />

        <div className={classes.options}>
          {!isAOView && userType === UserType.Ofqual && !disabled && (
            <>
              <Link
                component="button"
                className={classes.option}
                onClick={() =>
                  setFocussedDialog({
                    dialogActionType: DialogActionType.EditAction,
                    action,
                  })
                }
              >
                Edit
              </Link>
              {!isSent ? (
                <Link
                  component="button"
                  className={classes.option}
                  onClick={() =>
                    setFocussedDialog({
                      dialogActionType: DialogActionType.DeleteAction,
                      action,
                    })
                  }
                >
                  Delete
                </Link>
              ) : null}
            </>
          )}
          {userType === UserType.AO && (
            <Link
              component="button"
              className={classes.option}
              onClick={() =>
                setFocussedDialog({
                  dialogActionType: DialogActionType.ViewAction,
                  action,
                })
              }
            >
              View
            </Link>
          )}
          {userType === UserType.AO &&
            actionTypeCode === ActionTypeCode.SendMessage &&
            !action.status &&
            !disabled && (
              <Link
                component="button"
                className={classes.option}
                onClick={() =>
                  setFocussedDialog({
                    dialogActionType: DialogActionType.RespondToMessage,
                    action,
                  })
                }
              >
                Respond
              </Link>
            )}
          {userType === UserType.AO &&
            actionTypeCode === ActionTypeCode.ReadDocument &&
            !disabled && (
              <DownloadAllLink
                className={classes.option}
                action={action}
                organisationRecipientId={organisationRecipientId}
              />
            )}
          {userType === UserType.AO &&
            actionTypeCode === ActionTypeCode.UploadDocument &&
            !action.status &&
            !disabled && (
              <Link
                component="button"
                className={classes.option}
                onClick={() =>
                  setFocussedDialog({
                    dialogActionType: DialogActionType.RespondWithDocuments,
                    action,
                  })
                }
              >
                Upload Documents
              </Link>
            )}

          {userType === UserType.AO && !action.status && !disabled && (
            <MarkDocumentsAsReadLink
              className={classes.option}
              action={action}
              organisationRecipientId={organisationRecipientId}
              actionTypeCode={actionTypeCode}
            />
          )}
        </div>
      </Paper>
    </div>
  ) : null;
};

export const renderIcon = (actionType: ActionTypeCode) => {
  switch (actionType) {
    case ActionTypeCode.ReadDocument:
      return <DescriptionRounded color="secondary" fontSize="large" />;
    case ActionTypeCode.UploadDocument:
      return <PublishRounded color="secondary" fontSize="large" />;
    case ActionTypeCode.SendMessage:
      return <SmsRounded color="secondary" fontSize="large" />;
    default:
      return null;
  }
};
